import React, { useState, useEffect, useRef, useContext } from "react";
import { toast, ToastContainer } from "react-toastify";
import useCampaigns from "../../services/Whatsapp/useCampaigns";
import useLogs from "../../services/Whatsapp/useLogs";
import useWhatsappNumbers from "../../services/Whatsapp/useWhatsappNumbers";
import { GlobalContext } from "../../Context/GlobalContext";
import { exportAnyJsonToExcel } from "../../utils/exportAnyJsonToExcel";
import { Eye, Hash, RefreshCcw } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";
import NoIndex from "../../components/NoIndex";
import PaginationComponent from "../../utils/TablePagination";

const StatusBadge = ({ status }) => {
  const styles = {
    delivered: "bg-green-100 text-green-700 border-green-200",
    failed: "bg-red-100 text-red-600 border-red-200",
    accepted: "bg-blue-100 text-blue-700 border-blue-200",
    read: "bg-indigo-100 text-indigo-700 border-indigo-200",
    sent: "bg-purple-100 text-purple-700 border-purple-200",
    responded: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  return (
    <span
      className={`inline-flex justify-center items-center w-24 py-1 text-xs font-medium rounded-full border capitalize ${
        styles[status?.toLowerCase()] ||
        "bg-gray-100 text-gray-600 border-gray-200"
      }`}
    >
      {status || "—"}
    </span>
  );
};

const ITEMS_PER_PAGE = 15;

const Logs = () => {
  const { getCampaigns } = useCampaigns();
  const { getCampaignLogs, getAllMessageLogs } = useLogs();
  const { whatsappNumbers } = useWhatsappNumbers();
  const { userID } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState("campaign");
  const [campaigns, setCampaigns] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [campaignPage, setCampaignPage] = useState(1);
  const [selectedCampaignLogs, setSelectedCampaignLogs] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsModalLoading, setLogsModalLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedCampaignName, setSelectedCampaignName] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  const sortedCampaigns = [...(campaigns || [])].sort((a, b) => {
    const dateA = new Date(a.campaign_start || a.indate || a.created_at || 0);
    const dateB = new Date(b.campaign_start || b.indate || b.created_at || 0);

    return dateB - dateA; // latest first
  });

  const totalCampaignPages = Math.max(
    1,
    Math.ceil(sortedCampaigns.length / ITEMS_PER_PAGE),
  );

  const paginatedCampaigns = sortedCampaigns.slice(
    (campaignPage - 1) * ITEMS_PER_PAGE,
    campaignPage * ITEMS_PER_PAGE,
  );

  const getToday = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const [fromDate, setFromDate] = useState(getYesterday());
  const [toDate, setToDate] = useState(getToday());

  const fetchMessageLogs = async (pageNumber = 1) => {
    if (!hasMore && pageNumber !== 1) return;

    setLoading(true);

    const response = await getAllMessageLogs({
      userid: userID,
      fromDate,
      toDate,
      page: pageNumber.toString(),
      offset: "100",
    });

    if (response?.responseCode === "00") {
      const newData = response.response || [];

      if (pageNumber === 1) {
        setMessageLogs(newData);
      } else {
        setMessageLogs((prev) => [...prev, ...newData]);
      }

      if (newData.length < 100) {
        setHasMore(false); // no more data
      }
    } else {
      setHasMore(false);
    }

    setLoading(false);
  };

  const handlePreview = (item) => {
    setPreviewData({
      header: item.template_header,
      body: item.template_body,
      footer: item.template_footer,
      buttons: item.template_buttons || [],
    });

    setShowPreviewModal(true);
  };

  const handleUploadedExcelDownload = (item) => {
    const excelUrl = item?.excel_url || item?.excelUrl || item?.excelURL;

    if (!excelUrl) {
      toast.info("Uploaded Excel file not available");
      return;
    }

    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = `${item?.campaign_alias || item?.campaign_name || "campaign"}_uploaded.xlsx`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCampaignRefresh = async () => {
    if (!phoneNumber) {
      toast.info("Please select WhatsApp Number");
      return;
    }

    try {
      setLoading(true);
      const response = await getCampaigns(phoneNumber);
      setCampaigns(response || []);
      setCampaignPage(1);
      toast.success("Campaigns refreshed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to refresh campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = async (campaignId, statusType, campaignName) => {
    try {
      setSelectedCampaignLogs([]);
      setSelectedStatus(statusType.toUpperCase());
      setSelectedCampaignName(campaignName);
      setShowLogsModal(true);
      setLogsModalLoading(true);

      const response = await getCampaignLogs(campaignId, statusType);

      if (response?.responseCode === "00") {
        setSelectedCampaignLogs(response.response || []);
      } else {
        setSelectedCampaignLogs([]);
        toast.info("No Logs Found");
      }
    } catch (err) {
      console.error(err);
      setSelectedCampaignLogs([]);
      toast.error("Failed to load campaign logs");
    } finally {
      setLogsModalLoading(false);
    }
  };

  useEffect(() => {
    if (!whatsappNumbers || whatsappNumbers.length === 0) return;

    if (!phoneNumber) {
      setPhoneNumber(whatsappNumbers[0].phone_no_id);
    }
  }, [whatsappNumbers]);

  /* Load campaigns */
  useEffect(() => {
    if (activeTab !== "campaign") return;
    if (!phoneNumber) return;

    (async () => {
      setLoading(true);
      const response = await getCampaigns(phoneNumber);
      setCampaigns(response || []);
      setLoading(false);
    })();
  }, [phoneNumber, activeTab]);

  useEffect(() => {
    if (activeTab === "message") {
      fetchMessageLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "message") {
      setPage(1);
      setHasMore(true);
      fetchMessageLogs(1);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!observer.current) return;

    const intersection = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => {
            const nextPage = prev + 1;
            fetchMessageLogs(nextPage);
            return nextPage;
          });
        }
      },
      { threshold: 1 },
    );

    intersection.observe(observer.current);

    return () => intersection.disconnect();
  }, [hasMore, loading]);

  const handleApplyFilter = () => {
    setPage(1);
    setHasMore(true);
    setMessageLogs([]); // reset old data
    fetchMessageLogs(1); // force first page
  };

  /* Tabs UI */
  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 ${
        activeTab === id
          ? "border-blue-600 text-blue-600"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <div className="app-container flex flex-col h-screen p-5 montserrat-font-1">
        <ToastContainer />
        <NoIndex />

        <div className="w-full max-w-full mb-5 p-4 bg-white rounded-lg flex-shrink-0 montserrat-font-1">
          {/* Tabs */}
          <div className="flex gap-6 border-b mb-6">
            <TabButton id="campaign" label="Campaign Logs" />
            <TabButton id="message" label="Message Logs" />
          </div>

          {/* Header */}
          <div className="rounded-lg border border-gray-400 shadow-sm p-6 mb-2">
            {/* <div className="flex justify-between items-center mb-4"> */}
            <div className="flex flex-col md:flex-row lg:flex-nowrap lg:justify-between lg:items-center gap-4 mb-4">
              <h2 className="text-xl font-semibold">
                {activeTab === "campaign"
                  ? "WhatsApp Campaign Logs"
                  : "WhatsApp Message Logs"}
              </h2>

              {activeTab === "campaign" && (
                <button
                  onClick={handleCampaignRefresh}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500 text-white transition"
                >
                  <RefreshCcw className="w-5 h-5 text-white" />
                  Refresh
                </button>
              )}

              {activeTab === "message" && (
                // <div className="flex items-end gap-4">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full lg:w-auto">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">
                      From
                    </label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">
                      To
                    </label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleApplyFilter}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-md text-sm"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {activeTab === "campaign" && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  WhatsApp Number
                </label>
                <select
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select WhatsApp Number</option>
                  {whatsappNumbers?.map((num) => (
                    <option key={num.phone_no_id} value={num.phone_no_id}>
                      {num.phone_no_id} ({num.phone_no})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div
          className="flex-1 flex flex-col"
          style={{ overflow: "hidden", minHeight: 0 }}
        >
          {/* Campaign Logs Table */}
          {activeTab === "campaign" && (
            <div
              className="w-full rounded-xl border border-gray-200 bg-white p-4 flex flex-col"
              style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}
            >
              <div
                style={{
                  flex: "1 1 0",
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "auto",
                  borderRadius: "8px",
                }}
              >
                <table className="min-w-full border-separate border-spacing-y-0">
                  <thead className="bg-blue-500 text-white text-xs uppercase sticky top-0 z-20">
                    <tr>
                      <th className="px-5 py-3 whitespace-nowrap text-left">
                        Campaign Name/ID
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-left">
                        Start Time
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-left">
                        Template/Category
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Uploaded
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Duplicate
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Submitted
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Accepted
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Sent
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Delivered
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Read
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Responded
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-center">
                        Failed
                      </th>
                      <th className="px-5 py-3 whitespace-nowrap text-left">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan="13" className="text-center py-10">
                          <LoadingScreen text="Loading campaigns..." />
                        </td>
                      </tr>
                    ) : paginatedCampaigns.length === 0 ? (
                      <tr>
                        <td
                          colSpan="13"
                          className="text-center py-10 text-gray-400"
                        >
                          No Data Found
                        </td>
                      </tr>
                    ) : (
                      paginatedCampaigns.map((item, index) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  handleUploadedExcelDownload(item)
                                }
                                disabled={!item?.excel_url}
                                className={`font-medium text-start ${
                                  item?.excel_url
                                    ? "text-blue-600 cursor-pointer hover:underline"
                                    : "text-gray-400 cursor-not-allowed"
                                }`}
                                title={
                                  item?.excel_url
                                    ? "Download uploaded Excel"
                                    : "Uploaded Excel not available"
                                }
                              >
                                {item.campaign_alias ||
                                  item?.campaign_name ||
                                  "N/A"}
                              </button>

                              {/* ID Badge */}
                              <span className="inline-flex items-center gap-1 w-fit bg-green-100 text-gray-700 px-2 py-1 rounded-md text-xs font-medium">
                                <Hash className="w-3 h-3" />
                                {item.id}
                              </span>
                            </div>
                          </td>

                          <td className="px-5 py-3 whitespace-nowrap">
                            <div className="flex flex-col leading-tight">
                              <span className="text-sm font-semibold text-gray-900">
                                {new Date(
                                  item.campaign_start,
                                ).toLocaleDateString("en-GB")}
                              </span>
                              <span className="text-xs text-gray-800">
                                {new Date(
                                  item.campaign_start,
                                ).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          </td>

                          <td className="px-5 py-3 text-center relative">
                            <div className="flex flex-col leading-tight items-center">
                              {/* Preview only on Eye click */}
                              <div className="flex items-center gap-1 text-sm font-semibold">
                                <button
                                  type="button"
                                  onMouseEnter={() => setHoveredTemplate(item)}
                                  onMouseLeave={() => setHoveredTemplate(null)}
                                  onClick={() => handlePreview(item)}
                                  className="text-blue-600 hover:text-blue-800 cursor-pointer"
                                  title="Preview Template"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>

                                <span className="text-gray-800">
                                  {item.template_id || "N/A"}
                                </span>
                              </div>

                              {/* Category */}
                              <span className="text-xs text-gray-800">
                                {item.template_category || "N/A"}
                              </span>
                            </div>
                          </td>

                          <td className="px-5 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleUploadedExcelDownload(item)}
                              disabled={!item?.excel_url}
                              className={`font-medium ${
                                item?.excel_url
                                  ? "text-blue-600 cursor-pointer hover:underline"
                                  : "text-gray-400 cursor-not-allowed"
                              }`}
                              title={
                                item?.excel_url
                                  ? "Download uploaded Excel"
                                  : "Uploaded Excel not available"
                              }
                            >
                              {item.uploaded || 0}
                            </button>
                          </td>
                          <td className="px-5 py-3 text-center">
                            {item.duplicate || 0}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {item.submitted || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "accepted",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.accepted || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "sent",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.sent || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "delivered",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.delivered || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "read",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.read || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "responded",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.responded || 0}
                          </td>

                          <td
                            className="px-5 py-3 text-center text-blue-600 cursor-pointer hover:underline"
                            onClick={() =>
                              handleStatusClick(
                                item.id,
                                "failed",
                                item.campaign_alias,
                              )
                            }
                          >
                            {item.failed || 0}
                          </td>

                          <td className="px-5 py-3">
                            <span className="px-4 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex-shrink-0 border-t">
                <PaginationComponent
                  currentPage={campaignPage}
                  totalPages={totalCampaignPages}
                  onNextPage={() =>
                    setCampaignPage((p) => Math.min(p + 1, totalCampaignPages))
                  }
                  onPrevPage={() => setCampaignPage((p) => Math.max(p - 1, 1))}
                  onPageClick={(page) => setCampaignPage(page)}
                />
              </div>
            </div>
          )}

          {/* Message Logs */}
          {activeTab === "message" && (
            <div className="w-full rounded-xl border border-gray-200 bg-white p-4 flex flex-col flex-1 overflow-hidden">
              <div className="overflow-auto flex-1 rounded-lg">
                <table className="min-w-full border-separate border-spacing-y-0">
                  <thead className="bg-blue-500 text-white text-xs uppercase sticky top-0 z-20">
                    <tr>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        ID
                      </th>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Status
                      </th>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Date
                      </th>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Product
                      </th>
                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Category/Message
                      </th>

                      <th className="px-4 py-3 text-start whitespace-nowrap">
                        Error Code/Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading && messageLogs.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-10">
                          <LoadingScreen text="Loading logs..." />
                        </td>
                      </tr>
                    ) : messageLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan="10"
                          className="text-center py-10 text-gray-400"
                        >
                          No Logs Found
                        </td>
                      </tr>
                    ) : (
                      messageLogs.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-2 py-3">{item.id || "N/A"}</td>
                          <td className="px-2 py-3">
                            <div className="flex flex-col leading-tight max-w-[150px]">
                              <span className="font-medium text-gray-800 truncate">
                                {item.contact_name || "N/A"}
                              </span>

                              <span className="text-xs text-gray-800 truncate">
                                {item.contact || "N/A"}
                              </span>
                            </div>
                          </td>

                          <td className="px-2 py-3 whitespace-nowrap">
                            <StatusBadge status={item.status || "N/A"} />
                          </td>

                          <td className="px-2 py-3">
                            <div className="flex flex-col leading-tight">
                              <span className="text-sm font-medium text-gray-800">
                                {item.indate
                                  ? new Date(item.indate).toLocaleDateString()
                                  : "N/A"}
                              </span>
                              <span className="text-xs text-gray-800">
                                {item.indate
                                  ? new Date(item.indate).toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )
                                  : ""}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            {item.product || "N/A"}
                          </td>
                          <td className="px-2 py-3">
                            <div className="flex flex-col leading-tight">
                              <span className="text-sm font-medium text-gray-800">
                                {item.category || "N/A"}
                              </span>

                              <span className="text-xs text-gray-800">
                                {item.message_category || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3 max-w-[220px]">
                            <div className="flex flex-col leading-tight">
                              <span className="text-sm font-semibold text-red-600">
                                {item.error_code || "N/A"}
                              </span>

                              <span className="text-xs text-gray-600 break-words whitespace-normal">
                                {item.error || "N/A"}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!hasMore && messageLogs.length > 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    — End of Results —
                  </div>
                )}
                <div ref={observer}></div>
              </div>
            </div>
          )}
        </div>
        {/* Modal */}
        {showPreviewModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-[500px] shadow-xl">
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">
                  Message Preview
                </h2>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              {/* <div className="bg-gray-100 p-6"> */}
              <div className="bg-gray-100 p-6 flex flex-col justify-center items-center">
                {/* Chat Bubble */}
                <div className="bg-white rounded-lg p-4 shadow-sm relative border max-w-[380px]">
                  {/* Header Text */}
                  <p className="font-medium text-gray-800 mb-2">
                    {previewData?.header || "Offer Offer Offer"}
                  </p>

                  {/* Body Text */}
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    {previewData?.body ||
                      "Welcome to our restaurant. we are giving John Doe off on chinese food. book your order now."}
                  </p>

                  {/* Footer */}
                  <p className="text-xs text-gray-500 mt-3">
                    {previewData?.footer || "Powered By isanchaar"}
                  </p>

                  {/* Time */}
                  <span className="absolute bottom-2 right-3 text-xs text-gray-400">
                    {new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* Buttons */}
                <div className="mt-4 space-y-3 max-w-[380px]">
                  {previewData?.buttons?.length > 0 ? (
                    previewData.buttons.map((btn, index) => (
                      <button
                        key={index}
                        className="w-full border border-green-500 text-green-600 py-2 rounded-md text-sm hover:bg-green-50"
                      >
                        {btn.text}
                      </button>
                    ))
                  ) : (
                    <>
                      <button className="w-full border border-green-500 text-green-600 py-2 rounded-md text-sm hover:bg-green-50">
                        Interested
                      </button>
                      <button className="w-full border border-green-500 text-green-600 py-2 rounded-md text-sm hover:bg-green-50">
                        Not interested
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Footer Close Button */}
              <div className="flex justify-end px-6 py-4 border-t">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* logs modal */}

        {showLogsModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-[95%] max-w-6xl shadow-xl">
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">
                  {selectedCampaignName} – {selectedStatus}
                  <span className="ml-3 bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                    Total:{" "}
                    {logsModalLoading ? "..." : selectedCampaignLogs.length}
                  </span>
                </h2>

                <button
                  onClick={() => setShowLogsModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              {/* Table */}
              <div className="overflow-auto max-h-[65vh]">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-400 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">Contact Name</th>
                      <th className="px-4 py-3 text-left">Message Category</th>
                      <th className="px-4 py-3 text-left">Error Code</th>
                      <th className="px-4 py-3 text-left">Error Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    {logsModalLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-10">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                            <p className="text-sm font-semibold text-gray-500">
                              Loading logs...
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : selectedCampaignLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center py-8 text-gray-400"
                        >
                          No Data Found
                        </td>
                      </tr>
                    ) : (
                      selectedCampaignLogs.map((log) => (
                        <tr key={log.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">{log.contact || "N/A"}</td>

                          <td className="px-4 py-3">
                            <StatusBadge status={log.status || "N/A"} />
                          </td>

                          <td className="px-4 py-3">{log.indate || "N/A"}</td>

                          <td className="px-4 py-3">{log.category || "N/A"}</td>

                          <td className="px-4 py-3">
                            {log.contact_name || "N/A"}
                          </td>

                          <td className="px-4 py-3">
                            {log.message_category || "N/A"}
                          </td>

                          <td className="px-4 py-3">
                            {log.error_code || "N/A"}
                          </td>

                          <td className="px-4 py-3 text-xs text-gray-500 max-w-xs">
                            {log.error || "N/A"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-end gap-4 px-6 py-4 border-t">
                <button
                  onClick={() =>
                    exportAnyJsonToExcel({
                      data: selectedCampaignLogs,
                      filePrefix: `${selectedCampaignName}_${selectedStatus}`,
                      sheetName: "Logs",
                    })
                  }
                  disabled={
                    logsModalLoading || selectedCampaignLogs.length === 0
                  }
                  className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md text-sm disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Download Excel
                </button>

                <button
                  onClick={() => setShowLogsModal(false)}
                  className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-md text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Logs;
